import { useTranslation } from '@/composables/plugins';
import { destroyUserSession } from '@/helpers/session-helpers';
import { useActiveUserStore } from '@/stores/user';
import { useRoute, useRouter } from '@/composables/plugins';
import { nav, pathIsAllowed } from '@/modules/routing';
import { computed } from 'vue';
import type { DropdownMenuButton } from '@/components/common/types';
import { useAdminSession } from '@/composables/useAdminSession';
import { useFlag } from '@/composables/useFlag';
import baseURL from '@/helpers/base-url';
import i18next from '@/helpers/i18n';

const IS_PRODUCTION = import.meta.env.VITE_CUTR_ENV === 'production';

export function useNavigation() {
  const userStore = useActiveUserStore();
  const router = useRouter();
  const route = useRoute();
  const { t } = useTranslation();
  const { isExternalAdmin } = useAdminSession();
  const workOSEnabled = useFlag('workos-auth');
  const goToRoute = function (name: string) {
    if (['settings', 'signin', 'signup'].includes(name)) {
      router.push({ name }).catch((err) => console.log(err.message));
      return;
    }

    if (name === 'signout') {
      if (workOSEnabled.value && !userStore.superUserId) {
        const signoutUrl = new URL('/v2/auth/signout', baseURL);
        const lng = i18next.resolvedLanguage || i18next.language;
        if (lng) signoutUrl.searchParams.set('lng', lng);
        window.location.href = signoutUrl.href;
        return;
      }
      // Destroy session then reroute to signin.
      destroyUserSession().then(() => {
        router.push({ name: 'home' }).catch((err) => {
          console.log(err.message);
        });
      });
    }
  };

  const navLinks = computed(() => {
    const userType = userStore.activeSessionType || 'Public';
    const userRole = userStore.activeSessionRole || 'owner';

    let links = nav.filter(({ name, show }) => {
      if (
        show &&
        show({ userStore, isExternalAdmin: isExternalAdmin.value }) !== true
      )
        return false;

      return pathIsAllowed(userType, userRole, name);
    });

    if (userStore.activeSessionType === 'Maker') {
      links = links.map((n) => {
        if (n.name === 'workspace') {
          n.title = 'myActiveOrders';
        }
        return n;
      });
    }

    return links;
  });

  const accountMenuLinks = computed(() => {
    const signOutText = userStore.superUserId
      ? t('navigation:menu.backToAdmin')
      : t('navigation:menu.signOut');

    return (
      [
        userStore.canSwitch && {
          icon: 'sync',
          label: t('navigation:menu.switch-organisation'),
          to: {
            name: 'signin:selectOrganisation',
            query: { redirect: route.fullPath },
          },
          on: (hide) => ({ click: hide }),
        },
        {
          icon: 'person',
          label: t('navigation:menu.account'),
          to: {
            name: ['User', 'Admin'].includes(userStore.user.type ?? '')
              ? 'account'
              : 'settings',
          },
          on: (hide) => ({ click: hide }),
        },

        userStore.isAdmin &&
          !IS_PRODUCTION && {
            label: 'Dev Tools',
            icon: 'construction',
            to: { name: 'devTools:testData' },
            on: (hide) => ({ click: hide }),
          },

        {
          icon: 'logout',
          class: 'alert',
          label: signOutText,
          on: (hide) => ({
            click() {
              hide();
              goToRoute('signout');
            },
          }),
        },
      ] as DropdownMenuButton[]
    ).filter(Boolean);
  });

  const isSignedIn = computed(
    () =>
      userStore.activeSessionType &&
      !['Public', 'Unauthenticated'].includes(userStore.activeSessionType)
  );

  return { goToRoute, navLinks, accountMenuLinks, isSignedIn };
}
